/**
 * AI 紀行文セクション
 *
 * Markdown ライブラリを導入していないため、見出し・箇条書き・段落だけを
 * 最低限パースして描画する。
 */

import { Loader2, Sparkles } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type Props = {
  story: string | null
  generatedAt: string | null
  isGenerating: boolean
  onGenerate: () => void
  className?: string
}

type Block =
  | { kind: 'heading'; level: 1 | 2 | 3; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'paragraph'; text: string }

/** 見出し (#) と箇条書き (- / *) だけを解釈する簡易パーサ */
const parseStory = (markdown: string): Block[] => {
  const blocks: Block[] = []
  let listBuffer: string[] = []
  let paragraphBuffer: string[] = []

  const flushList = () => {
    if (listBuffer.length === 0) return
    blocks.push({ kind: 'list', items: listBuffer })
    listBuffer = []
  }
  const flushParagraph = () => {
    if (paragraphBuffer.length === 0) return
    blocks.push({ kind: 'paragraph', text: paragraphBuffer.join('\n') })
    paragraphBuffer = []
  }

  for (const rawLine of markdown.split('\n')) {
    const line = rawLine.trimEnd()

    const heading = /^(#{1,3})\s+(.*)$/.exec(line)
    if (heading?.[1] && heading[2]) {
      flushList()
      flushParagraph()
      const level = heading[1].length
      blocks.push({ kind: 'heading', level: level === 1 ? 1 : level === 2 ? 2 : 3, text: heading[2] })
      continue
    }

    const listItem = /^[-*]\s+(.*)$/.exec(line)
    if (listItem?.[1]) {
      flushParagraph()
      listBuffer.push(listItem[1])
      continue
    }

    if (line === '') {
      flushList()
      flushParagraph()
      continue
    }

    flushList()
    paragraphBuffer.push(line.replace(/\*\*/g, ''))
  }

  flushList()
  flushParagraph()
  return blocks
}

const HEADING_CLASS: Record<1 | 2 | 3, string> = {
  1: 'text-lg font-bold mt-4 first:mt-0',
  2: 'text-base font-semibold mt-4 first:mt-0',
  3: 'text-sm font-semibold mt-3 first:mt-0',
}

export const TripStorySection = ({ story, generatedAt, isGenerating, onGenerate, className }: Props) => {
  if (!story) {
    return (
      <div className={cn('rounded-xl border bg-card p-4 text-center', className)}>
        <Sparkles className="mx-auto mb-2 size-6 text-amber-500" />
        <p className="mb-3 text-sm text-muted-foreground">訪問記録と写真から、AI がこの旅の紀行文を書き起こします</p>
        <Button onClick={onGenerate} disabled={isGenerating} className="w-full">
          {isGenerating ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
          紀行文を生成
        </Button>
      </div>
    )
  }

  const blocks = parseStory(story)

  return (
    <div className={cn('rounded-xl border bg-card p-4', className)}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold">
          <Sparkles className="size-4 text-amber-500" />
          AI 紀行文
        </h2>
        <Button variant="ghost" size="sm" onClick={onGenerate} disabled={isGenerating} className="px-2!">
          {isGenerating ? <Loader2 className="size-4 animate-spin" /> : '書き直す'}
        </Button>
      </div>

      <div className="space-y-2 text-sm leading-relaxed">
        {blocks.map((block, i) => {
          if (block.kind === 'heading') {
            return (
              <p key={i} className={HEADING_CLASS[block.level]}>
                {block.text}
              </p>
            )
          }
          if (block.kind === 'list') {
            return (
              <ul key={i} className="list-disc space-y-1 pl-5">
                {block.items.map((item, j) => (
                  <li key={j} className="wrap-break-word">
                    {item}
                  </li>
                ))}
              </ul>
            )
          }
          return (
            <p key={i} className="wrap-break-word whitespace-pre-wrap">
              {block.text}
            </p>
          )
        })}
      </div>

      {generatedAt && (
        <p className="mt-3 text-[11px] text-muted-foreground">
          生成日時: {new Date(generatedAt).toLocaleString('ja-JP')}
        </p>
      )}
    </div>
  )
}
